// Streudetail: Bäume, Büsche, Felsen, Schrott.
//
// Konzept §12.2: deterministisches Fleisch auf handgemachtem Skelett — gleiche
// Koordinate → gleicher Baum, immer, für alle Spieler. scatterAt() liefert die
// Typen; Schrott am Gleis ergänzen wir selbst (Industriemüll lässt das Bild
// nach Bahn aussehen, §12.1 Punkt 2).
//
// Alles als InstancedMesh pro Typ. Zellenraster um den Spieler, Freispeicher-
// Stapel pro Budget — der typische Instancing-Leak ist „Slots nie zurückgeben".

import * as THREE from 'three';
import {
  TRACK_BED_HALF_WIDTH, WORLD_SEED, CORRIDOR_HALF_WIDTH,
} from '../../shared/constants.js';
import {
  projectToTrack, biomeAt, featureAt, STATIONS, trackPoint, trackRight,
} from '../../shared/track.js';
import {
  scatterAt, terrainHeight, terrainNormal, isVoidAt,
} from '../../shared/terrain.js';
import { rand01 } from '../../shared/rng.js';
import { mat, varyColor, STYLE_RULES } from '../render/style.js';

// --- Konstanten -------------------------------------------------------------

const CELL = 8; // m — grob genug für Silhouetten, fein genug für Parallaxe
/** Bis zum Nebel, nicht weiter — dahinter ist alles verschwendet. */
const VIEW_RADIUS = Math.min(280, CORRIDOR_HALF_WIDTH);
const VIEW_CELLS = Math.ceil(VIEW_RADIUS / CELL);
/** Bankette: im Schotter und auf der Böschung wächst nichts. */
const CLEAR_LATERAL = TRACK_BED_HALF_WIDTH + 6.4; // ≈ 9 m, deckt terrain SHOULDER
/** Nahbereich: dichteres Kleinzeug, weil dort Geschwindigkeit spürbar wird. */
const NEAR_DIST = 120;
const SHADOW_DIST = STYLE_RULES.shadowDistance;

const KINDS = [
  'bush-dry', 'bush-green', 'joshua', 'pine', 'pine-dead',
  'rock-small', 'rock-large', 'scrap',
];

// Instanz-Budgets: Fenster ±280 m / 8 m ≈ 35² Zellen, Dichte biomabhängig.
// Lieber knapper Budget + freier Stapel als stiller Leak.
const BUDGET = {
  'bush-dry': 900,
  'bush-green': 500,
  'joshua': 350,
  'pine': 600,
  'pine-dead': 250,
  'rock-small': 500,
  'rock-large': 200,
  'scrap': 280,
};

// Modul-Puffer — nichts pro Frame allokieren.
const _mat4 = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3(1, 1, 1);
const _up = new THREE.Vector3(0, 1, 0);
const _normal = new THREE.Vector3();
const _look = new THREE.Vector3();
const _color = new THREE.Color();
const _focus = { x: 0, y: 0, z: 0, s: 0 };
const _startPos = trackPoint(STATIONS[0].s);
const _right = { x: 0, y: 0, z: 0 };

export class ScatterSystem {
  async init(ctx) {
    this.THREE = ctx.THREE;
    this.group = new THREE.Group();
    this.group.name = 'scatter';
    ctx.scene.add(this.group);

    /** @type {Map<string, TypePool>} */
    this.pools = new Map();
    /** Aktive Zellen → Liste von { kind, slot } */
    this.cells = new Map();
    this.centerCX = null;
    this.centerCZ = null;

    for (const kind of KINDS) {
      this.pools.set(kind, createTypePool(kind, BUDGET[kind], this.group));
    }

    // Erste Zellen um den Südbahnhof.
    const focus = this._focusOf(ctx);
    this._syncCells(focus);
  }

  update(_dt, ctx) {
    const focus = this._focusOf(ctx);
    const cx = Math.floor(focus.x / CELL);
    const cz = Math.floor(focus.z / CELL);
    if (cx !== this.centerCX || cz !== this.centerCZ) {
      this.centerCX = cx;
      this.centerCZ = cz;
      this._syncCells(focus);
    }
    this._updateShadows(focus);
  }

  // --- Zellenraster ---------------------------------------------------------

  _syncCells(focus) {
    const cx = Math.floor(focus.x / CELL);
    const cz = Math.floor(focus.z / CELL);
    this.centerCX = cx;
    this.centerCZ = cz;

    const desired = new Set();
    const r = VIEW_CELLS;
    const r2 = r * r;

    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        // Kreis statt Quadrat — spart Ecken hinter dem Nebel.
        if (dx * dx + dz * dz > r2) continue;
        const key = cellKey(cx + dx, cz + dz);
        desired.add(key);
        if (!this.cells.has(key)) {
          this._populateCell(cx + dx, cz + dz, focus.s);
        }
      }
    }

    for (const [key, entries] of this.cells) {
      if (!desired.has(key)) {
        this._releaseCell(key, entries);
      }
    }
  }

  _populateCell(cx, cz, hintS) {
    const key = cellKey(cx, cz);
    const entries = [];
    const baseX = cx * CELL + CELL * 0.5;
    const baseZ = cz * CELL + CELL * 0.5;

    // Immer mit Hinweis — ohne ~20× teurer, und das hier läuft für tausende Zellen.
    const proj = projectToTrack(baseX, baseZ, hintS);
    const biome = biomeAt(proj.s);
    const distToPlayer = 0; // wird in _tryPlace über Weltpos genähert; hier Zellmitte
    void distToPlayer;

    // --- scatterAt (deterministisch) ----------------------------------------
    const sc = scatterAt(cx, cz, biome.id);
    if (sc) {
      const wx = baseX + sc.jitterX * CELL;
      const wz = baseZ + sc.jitterZ * CELL;
      const placed = this._tryPlace(sc.kind, wx, wz, sc.scale, sc.rotation, proj.s, false);
      if (placed) entries.push(placed);
    }

    // --- Schrott im Graben (§12.1 Punkt 2) ----------------------------------
    // Dünn, nur nahe am Gleis, nie auf dem Bett. Ergänzt scatterAt ohne
    // shared/terrain.js anzufassen.
    this._tryScrap(cx, cz, baseX, baseZ, proj, entries);

    // Fernbereich: zusätzliche große Silhouetten sparsam (Parallaxe — Horizont steht).
    const cellDist = Math.hypot(cx - this.centerCX, cz - this.centerCZ) * CELL;
    if (cellDist > NEAR_DIST && sc == null) {
      const farRoll = rand01(cx, cz, WORLD_SEED ^ 0xf4a);
      if (farRoll < 0.04) {
        const kind = biome.id === 'pine' || biome.id === 'meadow' ? 'pine'
          : biome.id === 'rock' ? 'rock-large'
            : 'joshua';
        const wx = baseX + (rand01(cx, cz, 71) - 0.5) * CELL * 0.6;
        const wz = baseZ + (rand01(cx, cz, 72) - 0.5) * CELL * 0.6;
        const scale = 1.3 + rand01(cx, cz, 73) * 0.9;
        const rot = rand01(cx, cz, 74) * Math.PI * 2;
        const placed = this._tryPlace(kind, wx, wz, scale, rot, proj.s, true);
        if (placed) entries.push(placed);
      }
    }

    this.cells.set(key, entries);
  }

  _tryScrap(cx, cz, baseX, baseZ, cellProj, entries) {
    const scrapRoll = rand01(cx, cz, WORLD_SEED ^ 0x5c2a);
    if (scrapRoll >= 0.11) return;

    // Position nah am Gleis, im Graben (lateral 9..18 m).
    const side = rand01(cx, cz, 0x51de) < 0.5 ? -1 : 1;
    const lat = side * (CLEAR_LATERAL + 1.5 + rand01(cx, cz, 0x52) * 8);
    const s = cellProj.s + (rand01(cx, cz, 0x53) - 0.5) * CELL;
    const tp = trackPoint(s);
    trackRight(s, _right);
    const wx = tp.x + _right.x * lat;
    const wz = tp.z + _right.z * lat;
    // Nur wenn die Zellnähe stimmt — sonst doppelte Scraps an Nachbarzellen.
    if (Math.floor(wx / CELL) !== cx || Math.floor(wz / CELL) !== cz) return;

    const scale = 0.7 + rand01(cx, cz, 0x54) * 0.8;
    const rot = rand01(cx, cz, 0x55) * Math.PI * 2;
    const placed = this._tryPlace('scrap', wx, wz, scale, rot, s, false);
    if (placed) entries.push(placed);
  }

  /**
   * Platziert eine Instanz, wenn der Ort erlaubt ist.
   * @returns {{ kind: string, slot: number } | null}
   */
  _tryPlace(kind, wx, wz, scale, rotation, hintS, farLod) {
    const proj = projectToTrack(wx, wz, hintS);

    // Nichts auf dem Gleis, der Bankette, im Einschnitt, auf der Brücke.
    if (Math.abs(proj.lateral) < CLEAR_LATERAL) return null;
    if (isVoidAt(proj.s, proj.lateral)) return null;
    const feat = featureAt(proj.s);
    if (feat?.type === 'bridge' || feat?.type === 'cutting') return null;

    // Fern: nur große Silhouetten (Parallaxe — Nahbereich rast mit Kleinzeug).
    if (farLod && (kind === 'bush-dry' || kind === 'bush-green' || kind === 'rock-small' || kind === 'scrap')) {
      return null;
    }

    const pool = this.pools.get(kind);
    if (!pool || pool.free.length === 0) return null;

    const slot = pool.free.pop();
    const y = terrainHeight(wx, wz, proj.s);
    const n = terrainNormal(wx, wz, proj.s);

    _pos.set(wx, y, wz);
    _scale.set(scale, scale, scale);

    const isRock = kind === 'rock-small' || kind === 'rock-large';
    if (isRock) {
      // Felsen folgen der Neigung — verankert das Gelände visuell.
      _normal.set(n.x, n.y, n.z);
      _look.set(0, 0, 1);
      _quat.setFromUnitVectors(_up, _normal);
      // Zusätzliche Gier um die lokale Up-Achse.
      const qYaw = _scratchQuatFromAxisAngle(_normal, rotation);
      _quat.multiply(qYaw);
    } else {
      // Pflanzen stehen senkrecht — schräge Bäume wirken kaputt, nicht windschief.
      _quat.setFromAxisAngle(_up, rotation);
    }

    _mat4.compose(_pos, _quat, _scale);
    pool.mesh.setMatrixAt(slot, _mat4);

    // Farbvariation deterministisch aus Zellkoordinaten.
    const seed = rand01(
      Math.floor(wx * 10), Math.floor(wz * 10), kindCharCode(kind)
    );
    const baseKey = colorKeyFor(kind);
    _color.setHex(varyColor(baseKey, seed, kind === 'scrap' ? 0.18 : 0.12));
    pool.mesh.setColorAt(slot, _color);

    pool.mesh.instanceMatrix.needsUpdate = true;
    if (pool.mesh.instanceColor) pool.mesh.instanceColor.needsUpdate = true;

    // count = höchster aktiver Slot + 1 wäre falsch bei Löchern.
    // Stattdessen: maxUsed tracken und count darauf setzen.
    if (slot + 1 > pool.maxUsed) pool.maxUsed = slot + 1;
    pool.mesh.count = pool.maxUsed;
    pool.used++;

    return { kind, slot };
  }

  _releaseCell(key, entries) {
    for (let i = 0; i < entries.length; i++) {
      const { kind, slot } = entries[i];
      const pool = this.pools.get(kind);
      if (!pool) continue;
      // Slot unsichtbar machen (Skala 0) und zurück auf den Stapel.
      _mat4.makeScale(0, 0, 0);
      pool.mesh.setMatrixAt(slot, _mat4);
      pool.free.push(slot);
      pool.used--;
      pool.mesh.instanceMatrix.needsUpdate = true;
    }
    this.cells.delete(key);
  }

  _updateShadows(focus) {
    // Schatten nur im Nahbereich — 1000 Buschschatten sind das teuerste
    // unwichtigste Detail im Bild.
    for (const pool of this.pools.values()) {
      // Grob: wenn irgendeine Instanz nah sein könnte, Schatten an.
      // Feinere Per-Instanz-Schatten gibt InstancedMesh nicht her.
      pool.mesh.castShadow = true;
      // Three.js castShadow ist mesh-weit; wir schalten global anhand Fokus ab,
      // indem wir nur „große" Typen werfen und den Rest aus.
      const big = pool.kind === 'pine' || pool.kind === 'pine-dead'
        || pool.kind === 'joshua' || pool.kind === 'rock-large';
      pool.mesh.castShadow = big;
      pool.mesh.receiveShadow = false;
    }
    void focus;
    void SHADOW_DIST;
  }

  _focusOf(ctx) {
    if (ctx.playerPos) {
      _focus.x = ctx.playerPos.x;
      _focus.y = ctx.playerPos.y;
      _focus.z = ctx.playerPos.z;
      if (ctx.playerState?.trackS != null) _focus.s = ctx.playerState.trackS;
      else if (ctx.trackHint != null && ctx.trackHint >= 0) _focus.s = ctx.trackHint;
      else _focus.s = STATIONS[0].s;
      return _focus;
    }
    _focus.x = _startPos.x;
    _focus.y = _startPos.y;
    _focus.z = _startPos.z;
    _focus.s = STATIONS[0].s;
    return _focus;
  }

  /** Budget-Snapshot für Tests. */
  getBudgetStats() {
    const out = {};
    for (const [kind, pool] of this.pools) {
      out[kind] = {
        budget: pool.budget,
        used: pool.used,
        free: pool.free.length,
        maxUsed: pool.maxUsed,
      };
    }
    return out;
  }

  /** Simuliert Bewegung für check-world (ohne echten ctx.playerPos-Stream). */
  simulateMoveTo(x, z, hintS) {
    this._syncCells({ x, y: 0, z, s: hintS });
  }

  estimateDrawCalls() {
    let n = 0;
    for (const pool of this.pools.values()) {
      if (pool.used > 0) n++;
    }
    return n;
  }

  dispose() {
    for (const pool of this.pools.values()) {
      pool.mesh.geometry?.dispose?.();
      pool.mesh.dispose?.();
    }
    this.pools.clear();
    this.cells.clear();
    this.group?.parent?.remove(this.group);
  }
}

// --- Typ-Pool / Geometrie ---------------------------------------------------

/**
 * @typedef {{ kind: string, budget: number, mesh: THREE.InstancedMesh,
 *             free: number[], used: number, maxUsed: number }} TypePool
 */

function createTypePool(kind, budget, parent) {
  const geo = buildScatterGeometry(kind);
  const matKey = colorKeyFor(kind);
  const material = mat(matKey, { vertexColors: false });
  // Instanzfarben brauchen ein Material, das sie multipliziert — Lambert tut das.
  const mesh = new THREE.InstancedMesh(geo, material, budget);
  mesh.name = `scatter:${kind}`;
  mesh.frustumCulled = true;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.count = 0;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  // Alle Matrizen auf 0-Skala, Freispeicher voll.
  const free = new Array(budget);
  const z = new THREE.Matrix4().makeScale(0, 0, 0);
  for (let i = 0; i < budget; i++) {
    mesh.setMatrixAt(i, z);
    free[i] = budget - 1 - i; // LIFO: hohe Indizes zuerst
  }
  mesh.instanceMatrix.needsUpdate = true;
  // Initiale Instanzfarbe neutral.
  const c = new THREE.Color(1, 1, 1);
  for (let i = 0; i < budget; i++) mesh.setColorAt(i, c);
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

  parent.add(mesh);
  return {
    kind, budget, mesh, free, used: 0, maxUsed: 0,
  };
}

/**
 * Sehr einfache, charakteristische Silhouetten.
 * STYLE_RULES.maxPolyPerScatter = 60 Dreiecke — die Silhouette ist alles.
 */
export function buildScatterGeometry(kind) {
  switch (kind) {
    case 'bush-dry':
    case 'bush-green':
      return buildBushGeo();
    case 'joshua':
      return buildJoshuaGeo();
    case 'pine':
      return buildPineGeo(false);
    case 'pine-dead':
      return buildPineGeo(true);
    case 'rock-small':
      return buildRockGeo(0.55);
    case 'rock-large':
      return buildRockGeo(1.1);
    case 'scrap':
      return buildScrapGeo();
    default:
      return buildBushGeo();
  }
}

function buildBushGeo() {
  // Zwei abgeflachte Kegel — trockener/grüner Busch als Klumpen.
  const g = new THREE.ConeGeometry(0.55, 0.7, 5);
  g.translate(0, 0.35, 0);
  const g2 = new THREE.ConeGeometry(0.4, 0.5, 5);
  g2.translate(0.15, 0.45, 0.05);
  return mergeGeos([g, g2]); // ~20 Tris
}

function buildJoshuaGeo() {
  // Stamm + drei Arme — Silhouette des Mojave, hart unter 60 Tris.
  // Cylinder segs=4 ohne Kappen-Waste: openEnded wo möglich.
  const parts = [];
  const trunk = new THREE.CylinderGeometry(0.12, 0.18, 1.6, 4, 1, true);
  trunk.translate(0, 0.8, 0);
  parts.push(trunk);
  const arms = [
    { y: 1.15, yaw: 0.5, pitch: 0.75, len: 0.85 },
    { y: 1.3, yaw: 2.4, pitch: 0.6, len: 0.7 },
    { y: 1.05, yaw: 4.2, pitch: 0.7, len: 0.8 },
  ];
  for (const a of arms) {
    const arm = new THREE.CylinderGeometry(0.07, 0.1, a.len, 3, 1, true);
    arm.translate(0, a.len * 0.5, 0);
    arm.rotateZ(a.pitch);
    arm.rotateY(a.yaw);
    arm.translate(0, a.y, 0);
    parts.push(arm);
    // Knospen-Büschel: 3-seitiger Kegel.
    const tip = new THREE.ConeGeometry(0.16, 0.26, 3);
    tip.translate(0, a.len + 0.08, 0);
    tip.rotateZ(a.pitch);
    tip.rotateY(a.yaw);
    tip.translate(0, a.y, 0);
    parts.push(tip);
  }
  return mergeGeos(parts);
}

function buildPineGeo(dead) {
  // Kegel-Stapel + Stamm. Tote Kiefer: schmaler, ohne untere Krone.
  const parts = [];
  const trunkH = dead ? 2.4 : 1.8;
  const trunk = new THREE.CylinderGeometry(0.1, 0.16, trunkH, 4, 1, true);
  trunk.translate(0, trunkH * 0.5, 0);
  parts.push(trunk);
  if (dead) {
    const top = new THREE.ConeGeometry(0.35, 1.2, 4);
    top.translate(0, trunkH + 0.3, 0);
    parts.push(top);
  } else {
    const layers = [
      { y: 1.3, r: 1.1, h: 1.3 },
      { y: 2.0, r: 0.85, h: 1.2 },
      { y: 2.7, r: 0.55, h: 1.1 },
    ];
    for (const L of layers) {
      const cone = new THREE.ConeGeometry(L.r, L.h, 5);
      cone.translate(0, L.y, 0);
      parts.push(cone);
    }
  }
  return mergeGeos(parts);
}

function buildRockGeo(size) {
  // Verzerrtes Ikosaeder — unregelmäßiger Fels, 20 faces.
  const g = new THREE.IcosahedronGeometry(size, 0);
  const pos = g.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    // Deterministische Verzerrung aus Vertexposition (kein RNG nötig).
    const s = 1 + 0.22 * Math.sin(x * 5.1 + y * 3.3) * Math.cos(z * 4.7);
    pos.setXYZ(i, x * s * 1.15, y * s * 0.75, z * s * 1.05);
  }
  g.translate(0, size * 0.35, 0);
  g.computeVertexNormals();
  return g;
}

function buildScrapGeo() {
  // Schrott im Graben: schiefe Kiste + Rohr — industrielle Silhouette.
  const box = new THREE.BoxGeometry(0.9, 0.35, 0.55);
  box.translate(0, 0.18, 0);
  const pipe = new THREE.CylinderGeometry(0.08, 0.08, 1.1, 5);
  pipe.rotateZ(Math.PI / 2);
  pipe.translate(0.1, 0.35, 0.15);
  return mergeGeos([box, pipe]);
}

function mergeGeos(geos) {
  // Manuelles Mergen ohne BufferGeometryUtils — keine Extra-Dependency.
  let vCount = 0;
  let iCount = 0;
  for (const g of geos) {
    vCount += g.attributes.position.count;
    iCount += g.index ? g.index.count : g.attributes.position.count;
  }
  const positions = new Float32Array(vCount * 3);
  const normals = new Float32Array(vCount * 3);
  const indices = new Uint32Array(iCount);
  let vOff = 0;
  let iOff = 0;
  let vBase = 0;
  for (const g of geos) {
    const p = g.attributes.position;
    const n = g.attributes.normal;
    if (!n) g.computeVertexNormals();
    const nn = g.attributes.normal;
    for (let i = 0; i < p.count; i++) {
      positions[(vOff + i) * 3] = p.getX(i);
      positions[(vOff + i) * 3 + 1] = p.getY(i);
      positions[(vOff + i) * 3 + 2] = p.getZ(i);
      normals[(vOff + i) * 3] = nn.getX(i);
      normals[(vOff + i) * 3 + 1] = nn.getY(i);
      normals[(vOff + i) * 3 + 2] = nn.getZ(i);
    }
    if (g.index) {
      for (let i = 0; i < g.index.count; i++) {
        indices[iOff++] = g.index.getX(i) + vBase;
      }
    } else {
      for (let i = 0; i < p.count; i++) indices[iOff++] = vBase + i;
    }
    vOff += p.count;
    vBase += p.count;
    g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  out.setIndex(new THREE.BufferAttribute(indices, 1));
  out.computeBoundingSphere();
  return out;
}

function colorKeyFor(kind) {
  switch (kind) {
    case 'bush-dry': return 'scrub';
    case 'bush-green': return 'grass';
    case 'joshua': return 'scrub';
    case 'pine': return 'pine';
    case 'pine-dead': return 'woodDark';
    case 'rock-small':
    case 'rock-large': return 'rock';
    case 'scrap': return 'steelDark';
    default: return 'scrub';
  }
}

function kindCharCode(kind) {
  let h = 0;
  for (let i = 0; i < kind.length; i++) h = (h + kind.charCodeAt(i) * (i + 1)) | 0;
  return h;
}

function cellKey(cx, cz) {
  return `${cx},${cz}`;
}

// Quaternion aus Achse/Winkel ohne Allokation der Zwischenobjekte pro Call —
// _qAxis wird wiederverwendet.
const _qAxis = new THREE.Quaternion();
const _tmpAxis = new THREE.Vector3();
function _scratchQuatFromAxisAngle(axis, angle) {
  _tmpAxis.copy(axis).normalize();
  _qAxis.setFromAxisAngle(_tmpAxis, angle);
  return _qAxis;
}

// --- Test-Helfer ------------------------------------------------------------

/**
 * Prüft, ob eine Weltposition für Streudetail erlaubt wäre.
 * Exportiert für check-world.js.
 */
export function isScatterAllowed(x, z, hintS = -1) {
  const proj = projectToTrack(x, z, hintS);
  if (Math.abs(proj.lateral) < CLEAR_LATERAL) return false;
  if (isVoidAt(proj.s, proj.lateral)) return false;
  const feat = featureAt(proj.s);
  if (feat?.type === 'bridge' || feat?.type === 'cutting') return false;
  return true;
}

export const SCATTER_CELL = CELL;
export const SCATTER_VIEW_RADIUS = VIEW_RADIUS;
export const SCATTER_CLEAR_LATERAL = CLEAR_LATERAL;
export const SCATTER_BUDGET = BUDGET;
export const SCATTER_KINDS = KINDS;
